import React from 'react';
import styled, { css } from 'styled-components';
import { Body5, Body7, VisuallyHidden } from '@ui';
import { GhostButton } from '@ui/buttons';
import { fetchCatalogItems } from '@services/fmdc';
import { DEFAULT_RESULTS_HEIGHT, resultsMaxHeight } from '@features/scoreBackfill/SpecSelect';
import { Mono } from './styles';

// A searchable picker over the catalog: `GET /catalog/items?q=` as the analyst types, a floating
// listbox, keyboard navigation. The typeahead face of scoreBackfill/SpecSelect, without the Browse
// face (a 300-item native select is not a browse) and against the server rather than memory.
//
// `value` is a catalog item code; `onChange(code, item)` fires on a pick (and with '' on clear).
// `label` is the field label; `excludeCodes` hides items already chosen elsewhere.

const SEARCH_DEBOUNCE_MS = 300;
const RESULTS_LIMIT = 25;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Field = styled.div`
  position: relative;

  input {
    width: 100%;
    border-radius: 6px;
    padding: 9px 36px 9px 11px;
    background: transparent;
    font-size: 14px;
    font-family: inherit;
    border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.themeColors.error : theme.themeColors.border)};
    color: ${({ theme }) => theme.themeColors.text};

    &::placeholder {
      color: ${({ theme }) => theme.themeColors.secondaryText};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
      outline-offset: 1px;
    }
  }
`;

const ClearButton = styled(GhostButton)`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.themeColors.secondaryText};
  font-size: 16px;

  &:hover {
    background: ${({ theme }) => theme.themeColors.hoverComponentBackground};
  }
`;

// The results float over whatever is below, SpecSelect's surface and tokens. The Page clips
// anything positioned past its bottom edge, so the max-height is measured against that edge
// (resultsMaxHeight) rather than fixed.
const Results = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 30;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const ResultRow = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  background: ${({ theme, $isActive }) => ($isActive ? theme.themeColors.primaryCtaButton : 'transparent')};
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};

  &:last-child {
    border-bottom: 0;
  }

  /* Only the highlighted row recolours its text; an idle row keeps the code in the text colour. */
  ${({ theme, $isActive }) =>
    $isActive &&
    css`
      * {
        color: ${theme.themeColors.buttonText};
      }
    `}
`;

const EmptyRow = styled.div`
  padding: 10px 12px;
`;

const Hint = styled(Body7)`
  color: ${({ theme, $invalid }) => ($invalid ? theme.themeColors.error : theme.themeColors.secondaryText)};
`;

let uid = 0;
const nextId = () => {
  uid += 1;
  return `catalog-item-picker-${uid}`;
};

export const itemDisplayLabel = (item) =>
  item ? `${item.canonical_item_label || item.label || item.canonical_item_code} (${item.canonical_item_code})` : '';

export const CatalogItemPicker = ({
  value,
  onChange,
  label = 'Catalog item',
  hint,
  invalid = false,
  placeholder = 'Type to search the catalog…',
  excludeCodes = [],
  status = 'active',
  selectedItem = null,
  autoFocus = false,
}) => {
  const [fieldId] = React.useState(nextId);
  const listboxId = `${fieldId}-results`;
  const inputRef = React.useRef(null);
  const [query, setQuery] = React.useState(null);
  const [results, setResults] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchError, setSearchError] = React.useState(null);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  // The item behind `value`, so the idle input can show its label; seeded from `selectedItem` when
  // the caller already holds it, otherwise it is the code until a pick replaces it.
  const [picked, setPicked] = React.useState(selectedItem);

  React.useEffect(() => {
    if (selectedItem && selectedItem.canonical_item_code === value) setPicked(selectedItem);
  }, [selectedItem, value]);

  const isEditing = query !== null;
  const isOpen = isEditing && query.trim() !== '';

  // Measured each time the list opens, and again on resize and on any scroll (capture, so a
  // scrolling ancestor counts too): both move the box relative to the edges that bound the list.
  // useLayoutEffect so the first paint of an open list already has its height.
  const [maxHeight, setMaxHeight] = React.useState(DEFAULT_RESULTS_HEIGHT);
  React.useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const measure = () => setMaxHeight(resultsMaxHeight(inputRef.current));
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen]);

  const idleLabel = value ? (picked && picked.canonical_item_code === value ? itemDisplayLabel(picked) : value) : '';
  const inputValue = isEditing ? query : idleLabel;

  // Debounced server search with the isCanceled guard; a stale response never overwrites a newer
  // query's results.
  React.useEffect(() => {
    if (!isOpen) {
      setResults([]);
      setIsSearching(false);
      return undefined;
    }
    const state = { isCanceled: false };
    setIsSearching(true);
    const timer = setTimeout(async () => {
      const result = await fetchCatalogItems({ q: query.trim(), status, limit: RESULTS_LIMIT });
      if (state.isCanceled) return;
      setIsSearching(false);
      if (result.error) {
        setSearchError(result.error);
        setResults([]);
      } else {
        setSearchError(null);
        const excluded = new Set(excludeCodes);
        setResults(result.items.filter((item) => !excluded.has(item.canonical_item_code)));
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      state.isCanceled = true;
      clearTimeout(timer);
    };
  }, [isOpen, query, status, excludeCodes.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (item) => {
    setPicked(item);
    onChange(item.canonical_item_code, item);
    setQuery(null);
    setActiveIndex(-1);
  };

  const closeAndRestore = () => {
    setQuery(null);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (!isOpen || results.length === 0) return;
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      if (!isOpen || results.length === 0) return;
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (!isOpen || results.length === 0) return;
      e.preventDefault();
      select(results[activeIndex >= 0 ? activeIndex : 0]);
    } else if (e.key === 'Escape') {
      if (!isEditing) return;
      e.preventDefault();
      closeAndRestore();
    }
  };

  const handleClear = () => {
    setPicked(null);
    onChange('', null);
    closeAndRestore();
    inputRef.current?.focus();
  };

  const optionId = (index) => `${listboxId}-option-${index}`;
  const hintText = searchError || hint;

  return (
    <Wrapper>
      {label && (
        <label htmlFor={fieldId}>
          <Body7>{label}</Body7>
        </label>
      )}
      <Field $invalid={invalid || !!searchError}>
        <input
          ref={inputRef}
          id={fieldId}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-invalid={invalid || undefined}
          autoComplete="off"
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onBlur={closeAndRestore}
          onFocus={(e) => e.target.select()}
        />
        {!!value && !isEditing && (
          <ClearButton aria-label={`Clear ${label.toLowerCase()}`} onClick={handleClear}>
            <span aria-hidden="true">×</span>
          </ClearButton>
        )}
        {isOpen && (
          <Results data-testid={`${fieldId}-results`} style={{ maxHeight }} onMouseDown={(e) => e.preventDefault()}>
            {isSearching && results.length === 0 ? (
              <EmptyRow>
                <Body5 isLowContrast>Searching…</Body5>
              </EmptyRow>
            ) : results.length === 0 ? (
              <EmptyRow>
                <Body5 isLowContrast>No catalog items match “{query.trim()}”</Body5>
              </EmptyRow>
            ) : (
              <div id={listboxId} role="listbox" aria-label="Matching catalog items">
                {results.map((item, index) => (
                  <ResultRow
                    key={item.canonical_item_code}
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === activeIndex}
                    $isActive={index === activeIndex}
                    onClick={() => select(item)}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <Body5>{item.canonical_item_label || item.canonical_item_code}</Body5>
                    <div>
                      <Mono>{item.canonical_item_code}</Mono>
                      {item.section && <Body7 isLowContrast> · {item.section}</Body7>}
                    </div>
                  </ResultRow>
                ))}
              </div>
            )}
          </Results>
        )}
      </Field>
      {hintText && <Hint $invalid={invalid || !!searchError}>{hintText}</Hint>}
      <VisuallyHidden aria-live="polite">
        {isOpen && !isSearching && `${results.length} matching catalog item${results.length === 1 ? '' : 's'}`}
      </VisuallyHidden>
    </Wrapper>
  );
};
